import { environment } from '../../environments/environment';
import { PortalType } from '../models/enum/shared/portal-type';
import { EmployerUser } from '../models/users/dto/employer-user';
import { RemittanceFrequencyEnum } from '../models/remittances/remittance-frequency';
import { AccountTypeIdEnum } from '../models/transactions/enum/account-type-id.enum';

export class Endpoints {
  static encodeParam(s: string): string {
    return encodeURIComponent(s);
  }

  static getBasePath(type: PortalType): string {
    switch (type) {
      case PortalType.Member:
        return 'members';
      case PortalType.Internal:
        return 'internalusers';
      case PortalType.Employer:
        return 'employers';
      default:
        throw new Error(`Invalid user type: ${type}`);
    }
  }

  static generateUrl(email: string, type: PortalType, pathSuffix: string): string {
    const basePath = this.getBasePath(type);
    return `${environment.mainServiceUrl}/${basePath}/${email}/${pathSuffix}`;
  }

  // Session

  static signIn(portalType: PortalType): string {
    return environment.mainServiceUrl + `/${this.getBasePath(portalType)}/login`;
  }

  static signOut(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'logout');
  }

  static refreshSession(email: string, portalType: PortalType): string {
    return this.generateUrl(email, portalType, 'refreshsession');
  }

  static changeEmail(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'changeemail');
  }

  static verifyEmail(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifyemail');
  }

  static changePassword(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'changepassword');
  }

  static changeMfaPhone(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'changemfaphonenumber');
  }

  static verifySmsCode(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifymfaphonenumber');
  }

  static verifyMfaToken(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifymfatokensetup');
  }

  static disableMfa(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'disablemfa');
  }

  static getVerifiedPhoneNumber(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifiedphonenumber');
  }

  static associateMfaToken(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'associatemfatoken');
  }

  // Member Auth

  static verifyMember(memberInviteId: string): string {
    return environment.mainServiceUrl + `/invites/${memberInviteId}/verify`;
  }

  static confirmMember(memberInviteId: string): string {
    return environment.mainServiceUrl + `/invites/${memberInviteId}/complete`;
  }

  static verifyMemberEmail(): string {
    return environment.mainServiceUrl + `/members/confirmmember`;
  }

  // Forgot Password

  static generateForgotPasswordUrl(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'forgotpassword');
  }

  static generateResetPasswordUrl(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'resetforgottenpassword');
  }

  // Eula

  static getEula(portalTypeId: number): string {
    return environment.mainServiceUrl + `/portaltypes/${portalTypeId}/enduserlicenceagreements`;
  }

  // Members
  static ipCreateMember(): string {
    return environment.mainServiceUrl + `/internal/member`;
  }

  static ipCreateNonMember(): string {
    return environment.mainServiceUrl + `/internal/nonmember`;
  }

  static updateMember(memberId: string): string {
    return environment.mainServiceUrl + `/members/${memberId}`;
  }

  static updateMemberOnboardingInformation(): string {
    return environment.mainServiceUrl + `/member/onboardinginformation`;
  }

  static getEmployerDetails(memberInviteId: string): string {
    return environment.mainServiceUrl + `/invites/${memberInviteId}/employerdetails`;
  }

  static ipGetMemberById(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}`;
  }

  static ipGetMemberRetirementStub(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/retirementstubs`;
  }

  static ipUpdateMemberRetirementStub(memberId: number, retirementStubId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/retirementstubs/${retirementStubId}`;
  }

  static ipGetMemberLifeStatus(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/lifestatus`;
  }

  static ipUpdateMemberLifeStatus(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/lifestatus`;
  }

  static ipGetMemberPaymentSuspension(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paymentsuspension`;
  }

  static ipSuspendMemberPayments(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paymentsuspension/suspend`;
  }

  static ipResumeMemberPayments(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paymentsuspension/resume`;
  }

  static ipGetMemberGarnishmentOrders(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/garnishmentorders`;
  }

  static ipCreateMemberGarnishmentOrder(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/garnishmentorders`;
  }

  static ipCancelMemberGarnishmentOrder(memberId: number, orderId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/garnishmentorders/${orderId}/cancel`;
  }

  static ipRestrictMemberPortalAccess(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/restrictportalaccess`;
  }

  static ipRestoreMemberPortalAccess(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/restoreportalaccess`;
  }

  static ipUpdateThirdPartyContributionConsent(): string {
    return environment.mainServiceUrl + `/internal/members/thirdpartycontributionconsent`;
  }

  static mpUpdateThirdPartyContributionConsent(): string {
    return environment.mainServiceUrl + `/member/thirdpartycontributionconsent`;
  }

  static getRetirementTypes(): string {
    return environment.mainServiceUrl + `/retirementtypes`;
  }

  static mpGetMemberRetirementType(): string {
    return environment.mainServiceUrl + `/member/retirementtype`;
  }

  static getLifeStatusTypes(): string {
    return environment.mainServiceUrl + `/lifestatustypes`;
  }

  static getDeathRecordContactMethodTypes(): string {
    return environment.mainServiceUrl + `/deathrecordcontactmethodtypes`;
  }

  static getDeathRecordSettlementTypes(): string {
    return environment.mainServiceUrl + `/deathrecordsettlementtypes`;
  }

  static ipGetMemberDeathRecord(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords`;
  }

  static ipCreateMemberDeathRecord(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords`;
  }

  static ipCreateDeathRecordReminder(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/reminders`;
  }

  static ipDeleteDeathRecordReminder(memberId: number, deathRecordId: number, reminderId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/reminders/${reminderId}`
    );
  }

  static ipUpdateDeathRecordAdminDetails(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/admindetails`;
  }

  static ipDeathRecordNotifyingParty(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/notifyingparty`;
  }

  static ipDeathRecordLegalRepresentative(memberId: number, deathRecordId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/legalrepresentative`
    );
  }

  static ipCreateDeathRecordSettlement(memberId: number, deathRecordId: number, deathRecordAccountId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/deathrecords/${deathRecordId}/accounts/${deathRecordAccountId}/settlements`
    );
  }

  static ipDeathRecordSettlement(memberId: number, deathRecordId: number, settlementId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/deathrecords/${deathRecordId}/settlements/${settlementId}`
    );
  }

  static ipReleaseDeathRecordSettlement(memberId: number, deathRecordId: number, settlementId: number): string {
    return Endpoints.ipDeathRecordSettlement(memberId, deathRecordId, settlementId) + `/release`;
  }

  static ipCreateDeathRecordAttachment(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/attachments`;
  }

  static ipDeleteDeathRecordAttachment(memberId: number, deathRecordId: number, attachmentId: string): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/deathrecords/${deathRecordId}/attachments/${attachmentId}`
    );
  }

  static ipCloseDeathRecord(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/close`;
  }

  static ipReopenDeathRecord(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/reopen`;
  }

  static ipArchiveDeathRecord(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/archive`;
  }

  static ipGetDeathRecordComments(deathRecordId: number): string {
    return environment.mainServiceUrl + `/odata/internal/deathrecords/${deathRecordId}/comments`;
  }

  static ipAddDeathRecordComment(deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/deathrecords/${deathRecordId}/comments`;
  }

  static getTransferInSourceAccountTypes(): string {
    return environment.mainServiceUrl + `/transferinsourceaccounttypes`;
  }

  static getFinancialInstitutionTypes(): string {
    return environment.mainServiceUrl + `/financialinstitutiontypes`;
  }

  static getMemberContributionFrequencyTypes(): string {
    return environment.mainServiceUrl + `/membercontributionfrequencytypes`;
  }

  static getCreditCardStatusTypes(): string {
    return environment.mainServiceUrl + `/creditcardstatustypes`;
  }

  static ipGetMemberPaymentMethods(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paymentmethods`;
  }

  static mpGetMemberPaymentMethods(): string {
    return environment.mainServiceUrl + `/member/paymentmethods`;
  }

  static ipCreateMemberCreditCard(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/creditcards`;
  }

  static ipGetMemberCreditCards(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/creditcards`;
  }

  static ipUpdateMemberCreditCardStatus(memberId: number, creditCardId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/creditcards/${creditCardId}/updatestatus`;
  }

  static ipGetMemberCreditCardUsage(memberId: number, creditCardId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/creditcards/${creditCardId}/usage`;
  }

  static ipCreateMemberContribution(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/contributions`;
  }

  static ipCreateMemberScheduledContribution(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/scheduledcontributions`;
  }

  static ipGetMemberScheduledContributions(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/scheduledcontributions`;
  }

  static mpGetMemberScheduledContributions(): string {
    return environment.mainServiceUrl + `/member/scheduledcontributions`;
  }

  static ipCancelMemberScheduledContribution(memberId: number, scheduledContributionId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/scheduledcontributions/${scheduledContributionId}/cancel`
    );
  }

  static ipGetNextFiveScheduledContributionDates(
    memberId: number,
    frequencyId: number,
    startDate: string,
    endDate: string | null
  ): string {
    const endDateParam = endDate ? `&endDate=${endDate}` : '';
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/scheduledcontributions/nextfivedates` +
      `?frequencyId=${frequencyId}&startDate=${startDate}${endDateParam}`
    );
  }

  static mpCreateMemberCreditCard(): string {
    return environment.mainServiceUrl + `/member/creditcards`;
  }

  static mpGetMemberCreditCards(): string {
    return environment.mainServiceUrl + `/member/creditcards`;
  }

  static mpUpdateMemberCreditCardStatus(creditCardId: number): string {
    return environment.mainServiceUrl + `/member/creditcards/${creditCardId}/updatestatus`;
  }

  static mpGetMemberCreditCardUsage(creditCardId: number): string {
    return environment.mainServiceUrl + `/member/creditcards/${creditCardId}/usage`;
  }

  static mpCreateMemberContribution(): string {
    return environment.mainServiceUrl + `/member/contributions`;
  }

  static mpGetSpousalContributionsYearToDate(): string {
    return environment.mainServiceUrl + `/member/contributions/spousalcontributions`;
  }

  static mpCreateMemberScheduledContribution(): string {
    return environment.mainServiceUrl + `/member/scheduledcontributions`;
  }

  static mpCancelMemberScheduledContribution(scheduledContributionId: number): string {
    return environment.mainServiceUrl + `/member/scheduledcontributions/${scheduledContributionId}/cancel`;
  }

  static mpGetNextFiveScheduledContributionDates(
    frequencyId: number,
    startDate: string,
    endDate: string | null
  ): string {
    const endDateParam = endDate ? `&endDate=${endDate}` : '';
    return (
      environment.mainServiceUrl +
      `/member/scheduledcontributions/nextfivedates?frequencyId=${frequencyId}&startDate=${startDate}${endDateParam}`
    );
  }

  static ipGetNewMembersToVerify(): string {
    return environment.mainServiceUrl + `/internal/newmemberverification`;
  }

  static ipGetMemberActiveVariableBenefitConfigSubmission(memberId: number): string {
    return (
      environment.mainServiceUrl + `/odata/internal/members/${memberId}/activevariablebenefitconfigurationsubmission`
    );
  }

  static getEmployerMemberById(memberId: number): string {
    return environment.mainServiceUrl + `/odata/employer/members/${memberId}`;
  }

  static epGetMemberSubmissions(memberId: number): string {
    return environment.mainServiceUrl + `/employer/members/${memberId}/submissions`;
  }

  static getMemberById(): string {
    return environment.mainServiceUrl + `/odata/member/members`;
  }

  static getMemberSIN(): string {
    return environment.mainServiceUrl + `/odata/member/socialinsurancenumber`;
  }

  static memberForceChangeEmail(memberId: string): string {
    return environment.mainServiceUrl + `/members/${memberId}/forcechangeemail`;
  }

  static memberComments(memberId: string): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/comments`;
  }

  static memberForceChangePassword(memberId: string): string {
    return environment.mainServiceUrl + `/members/${memberId}/forceresetpassword`;
  }

  static searchInternalMembersByIdOrName(): string {
    return environment.mainServiceUrl + `/odata/internal/membersearchbyidorname`;
  }

  static searchEmployerMembersByIdOrName(): string {
    return environment.mainServiceUrl + `/odata/employer/membersearchbyidorname`;
  }

  static getAllInternalMembers(): string {
    return environment.mainServiceUrl + `/odata/internal/members`;
  }

  static getAllMembers(): string {
    return environment.mainServiceUrl + `/odata/members`;
  }

  static mpGetMember(): string {
    return environment.mainServiceUrl + `/odata/member/members`;
  }

  static ipCreateMembers(): string {
    return environment.mainServiceUrl + `/internal/members`;
  }

  static updateInternalEmploymentHistory(memberId: string, employerHistoryId: string): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/employmenthistory/${employerHistoryId}`;
  }

  static updateEmploymentHistory(memberId: string, employerHistoryId: string): string {
    return environment.mainServiceUrl + `/members/${memberId}/employmenthistory/${employerHistoryId}`;
  }

  static createEmploymentHistory(memberId: string): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/employmenthistory`;
  }

  static resendMemberInvite(memberId: string): string {
    return environment.mainServiceUrl + `/members/${memberId}/resendinvite`;
  }

  static ipUpdateMemberSpouseContactInfo(memberId: number, spouseId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/spouses/${spouseId}/contactinformation`;
  }

  static mpUpdateSpouseContactInfo(spouseId: number): string {
    return environment.mainServiceUrl + `/member/spouses/${spouseId}/contactinformation`;
  }

  static addHistoricalRelationshipStatus(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/historicalrelationshipstatus`;
  }

  // Types

  static getStatesByCountryId(countryId: number): string {
    return environment.mainServiceUrl + `/countrytypes/${countryId}/statetypes`;
  }

  static getGenders(): string {
    return environment.mainServiceUrl + `/gendertypes`;
  }

  static getPronouns(): string {
    return environment.mainServiceUrl + `/pronountypes`;
  }

  static getCountries(): string {
    return environment.mainServiceUrl + `/countrytypes`;
  }

  static getCountriesWithStates(): string {
    return environment.mainServiceUrl + `/countrieswithstates`;
  }

  static getSubmissionStatusTypes(): string {
    return environment.mainServiceUrl + `/submissionstatustypes`;
  }

  static getAlertTypes(): string {
    return environment.mainServiceUrl + `/alertTypes`;
  }

  static getContributionEntryOptionTypes(): string {
    return environment.mainServiceUrl + `/transactionentrycodetypes?categoryId=1`;
  }

  static getTransactionEntryCodeTypes(): string {
    return environment.mainServiceUrl + `/transactionentrycodetypes`;
  }

  static getTransactionStatusTypes(): string {
    return environment.mainServiceUrl + `/transactionstatustypes`;
  }

  static getMfaTypes(): string {
    return environment.mainServiceUrl + `/multifactorauthenticationtypes`;
  }

  static getMemberEmploymentStatusTypes(): string {
    return environment.mainServiceUrl + `/memberemploymentstatustypes`;
  }

  static getMemberEmploymentStatusChangeReasonTypes(): string {
    return environment.mainServiceUrl + `/memberemploymentstatuschangereasontypes`;
  }

  static getMemberRelationshipTypes(): string {
    return environment.mainServiceUrl + `/memberrelationshipstatustypes`;
  }

  static getFundTypes(): string {
    return environment.mainServiceUrl + `/fundtypes`;
  }

  static getAccountTypes(): string {
    return environment.mainServiceUrl + `/accounttypes`;
  }

  static getAccountStatusTypes(): string {
    return environment.mainServiceUrl + `/accountstatustypes`;
  }

  static getBeneficiaryRelationshipToMemberTypes(): string {
    return environment.mainServiceUrl + `/beneficiaryrelationshiptomembertypes`;
  }

  // fiduciaries

  static getFiduciaryTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciarytypes`;
  }

  static getFiduciaryEntityTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciaryentitytypes`;
  }

  static getFiduciaryPowerOfAttorneyTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciarypowerofattorneytypes`;
  }

  static getFiduciaryPowerOfAttorneyAppointmentTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciarypowerofattorneyappointmenttypes`;
  }

  // Internal Users

  static getInternalUsers(): string {
    return environment.mainServiceUrl + `/odata/internal/internalusers`;
  }

  static getInternalUser(internalUserId: number): string {
    return environment.mainServiceUrl + `/odata/internal/internalusers/${internalUserId}`;
  }

  static updateInternalUser(internalUserId: string): string {
    return environment.mainServiceUrl + `/internal/internalusers/${internalUserId}`;
  }

  static forceResetInternalUserPassword(internalUserId: string): string {
    return environment.mainServiceUrl + `/internal/internalusers/${internalUserId}/forceresetpassword`;
  }

  static forceResetInternalUserEmail(internalUserId: string): string {
    return environment.mainServiceUrl + `/internal/internalusers/${internalUserId}/forcechangeemail`;
  }

  static createInternalUser(): string {
    return environment.mainServiceUrl + `/internal/internalusers`;
  }

  // Roles and Permissions
  static getMemberUserRoles(userId: string): string {
    return environment.mainServiceUrl + `/odata/memberusers/${userId}/roles`;
  }

  static getEmployerUserRoles(): string {
    return environment.mainServiceUrl + `/odata/employer/tokenholderroles`;
  }

  static getInternalUserRoles(userId: string): string {
    return environment.mainServiceUrl + `/odata/internal/internalusers/${userId}/roles`;
  }

  static ipGetInternalUserSubmissionPermissions(userId: number): string {
    return environment.mainServiceUrl + `/internal/internalusers/${userId}/submissionpermissions`;
  }

  static ipGetUserRoles(): string {
    return environment.mainServiceUrl + `/odata/internal/internalroles`;
  }

  static epGetUserRoles(): string {
    return environment.mainServiceUrl + `/odata/employer/roles`;
  }

  static ipGetEmployerUserRoles(): string {
    return environment.mainServiceUrl + `/odata/internal/employerroles`;
  }

  // Employers

  static epSelfEnrol() {
    return environment.mainServiceUrl + `/employer/selfenrol`;
  }

  static epGetUsersForEmployer(): string {
    return environment.mainServiceUrl + `/odata/employer/users`;
  }

  static epCreateEmployerUser(): string {
    return environment.mainServiceUrl + `/employer/users`;
  }

  static epGetEmployerUserById(userId: number): string {
    return environment.mainServiceUrl + `/odata/employer/users/${userId}`;
  }

  static epUpdateEmployerUser(userId: number): string {
    return environment.mainServiceUrl + `/employer/users/${userId}`;
  }

  static ipCreateEmployerUser(employerId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/users`;
  }

  static createEmployee(): string {
    return environment.mainServiceUrl + `/employer/member`;
  }

  static createEmployees(): string {
    return environment.mainServiceUrl + `/employer/members`;
  }

  static ipGetEmployerUserById(employerId: number, userId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/users/${userId}`;
  }

  static ipGetEmployerById(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}`;
  }

  static ipGetUsersForEmployer(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/users`;
  }

  static ipGetEmployerMembers(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/members`;
  }

  static ipUpdateEmployerUser(employerId: number, userId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/users/${userId}`;
  }

  static ipForceChangeEmployerUserPassword(employerId: number, userId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/users/${userId}/forceresetpassword`;
  }

  static ipForceChangeEmployerUserEmail(employerId: number, userId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/users/${userId}/forcechangeemail`;
  }

  static employerForceChangeEmail(u: EmployerUser): string {
    return environment.mainServiceUrl + `/employer/users/${u?.id}/forcechangeemail`;
  }

  static employerForceChangePassword(u: EmployerUser): string {
    return environment.mainServiceUrl + `/employer/users/${u?.id}/forceresetpassword`;
  }

  static searchEmployersByIdOrName(): string {
    return environment.mainServiceUrl + `/odata/internal/employersearchbyidorname`;
  }

  static epCreateEmployees(): string {
    return environment.mainServiceUrl + `/employer/members`;
  }

  static epGetEmployerMembers(): string {
    return environment.mainServiceUrl + `/odata/employer/members`;
  }

  static employerStatusTypes(): string {
    return environment.mainServiceUrl + `/employerstatustypes`;
  }

  static memberStatusTypes(): string {
    return environment.mainServiceUrl + `/memberstatustypes`;
  }

  static ipGetEmployers(): string {
    return environment.mainServiceUrl + `/odata/internal/employers`;
  }

  static ipUpdateEmployer(employerId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}`;
  }

  static ipUpdateEmployerContacts(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/updatecontacts`;
  }

  static epUpdateEmployerContacts(): string {
    return environment.mainServiceUrl + `/employer/submissions/updatecontacts`;
  }

  static epGetEmployer(): string {
    return environment.mainServiceUrl + `/odata/employer/employers`;
  }

  static epGetEmployerSubmissionById(submissionId: string): string {
    return environment.mainServiceUrl + `/odata/employer/submissions/${submissionId}`;
  }

  static epGetEmployerSubmissions(): string {
    return environment.mainServiceUrl + `/odata/employer/submissions/`;
  }

  // Submissions

  static ipGetMemberSubmissions(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissions`;
  }

  static ipGetEmployerSubmissions(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissions`;
  }

  static ipGetSharedSubmissions(): string {
    return environment.mainServiceUrl + `/odata/internal/sharedsubmissions`;
  }

  static getMemberSubmissions(): string {
    return environment.mainServiceUrl + `/odata/member/submissions`;
  }

  static ipGetMemberSubmissionById(id: string): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissions/${id}`;
  }

  static ipGetEmployerSubmissionById(submissionId: string): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissions/${submissionId}`;
  }

  static getMemberSubmissionById(submissionId: string): string {
    return environment.mainServiceUrl + `/odata/member/submissions/${submissionId}`;
  }

  static ipGetMemberSubmissionViewersForMemberSubmissionType(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissionviewers`;
  }

  static ipGetEmployerSubmissionViewersForEmployerSubmissionType(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissionviewers`;
  }

  static ipGetMemberCommentMentionables(): string {
    return environment.mainServiceUrl + `/odata/internal/membercommentmentionableusers`;
  }

  static ipGetEmployerCommentMentionables(): string {
    return environment.mainServiceUrl + `/odata/internal/employercommentmentionableusers`;
  }

  static ipGetCommentsForEmployer(employerId: string): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/comments`;
  }

  static ipAddEmployerRecordComment(employerId: string): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/comments`;
  }

  static getMemberOnboardingProgress(): string {
    return environment.mainServiceUrl + `/odata/member/onboardingprogress`;
  }

  static ipGetActiveRemittancesForMember(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/activeremittances`;
  }

  static createInternalNameChangeSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/namechange`;
  }

  static createNameChangeSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/namechange`;
  }

  static createInternalAddressChangeSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/addresschange`;
  }

  static createAddressChangeSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/addresschange`;
  }

  static createInternalPersonalDetailsSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/personaldetails`;
  }

  static createPersonalDetailsSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/personaldetails`;
  }

  static createInternalUpdatePhoneNumberSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/phonenumber`;
  }

  static createUpdatePhoneNumberSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/phonenumber`;
  }

  static createMemberContributionInvestmentInstructionsSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/contributionaccounts/investmentinstructions`;
  }

  static createMemberVariableBenefitInvestmentInstructionsSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/variablebenefitaccounts/investmentinstructions`;
  }

  static createIPMemberInvestmentInstructionsSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/investmentinstructions`;
  }

  static mpCreateRelationshipStatusSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/relationshipstatus`;
  }

  static ipCreateRelationshipStatusSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/relationshipstatus`;
  }

  static mpCreateContributionAccountBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/updatecontributionbeneficiary`;
  }

  static mpCreateVBAccountBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/updatevariablebenefitbeneficiary`;
  }

  static mpCreateAnnuityAccountBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/updateannuitybeneficiary`;
  }

  static ipCreateContributionAccountBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/updatecontributionbeneficiary`;
  }

  static ipCreateVBAccountBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/updatevariablebenefitbeneficiary`;
  }

  static ipCreateAnnuityAccountBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/updateannuitybeneficiary`;
  }

  static mpCreateBankingInformationSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/bankinginformation`;
  }

  static ipCreateBankingInformationSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/bankinginformation`;
  }

  static mpCreateTransferInSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/transferin`;
  }

  static ipCreateTransferInSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/transferin`;
  }

  static ipCreateLegalFiduciarySubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/legalfiduciary`;
  }

  static ipCreateMemberSubmissionAttachment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/attachments`;
  }

  static ipCreateEmployerSubmissionAttachment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/attachments`;
  }

  static ipDeleteMemberSubmissionAttachment(attachmentId: string, submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/attachments/${attachmentId}`;
  }

  static ipDeleteEmployerSubmissionAttachment(attachmentId: string, submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/attachments/${attachmentId}`;
  }

  static ipGetMemberSubmissionAttachments(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/attachments`;
  }

  static ipGetEmployerSubmissionAttachments(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/attachments`;
  }

  static ipAddMemberSubmissionComment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/comments`;
  }

  static ipAddEmployerSubmissionComment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/comments`;
  }

  static ipUserCancelMemberSubmission(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/cancel`;
  }

  static ipUserCancelEmployerSubmission(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/cancel`;
  }

  static ipSetMemberSubmissionToMemberActionRequired(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/memberactionrequired`;
  }

  static ipSetEmployerSubmissionToEmployerActionRequired(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/employeractionrequired`;
  }

  static ipSetMemberSubmissionToRejected(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/rejected`;
  }

  static ipSetEmployerSubmissionToRejected(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/rejected`;
  }

  static ipSetMemberSubmissionToProcessing(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/processing`;
  }

  static ipSetEmployerSubmissionToProcessing(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/processing`;
  }

  static ipSetMemberSubmissionToApproved(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/approved`;
  }

  static ipSetEmployerSubmissionToApproved(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/approved`;
  }

  static ipSetMemberSubmissionToReadyForApproval(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/readyforapproval`;
  }

  static ipSetEmployerSubmissionToReadyForApproval(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/readyforapproval`;
  }

  static ipSetRelationshipStatusSubmissionToApproved(submissionId: number): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/relationshipstatusapproved`;
  }

  static ipGetMemberSubmissionApprovers(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissionapprovers`;
  }

  static ipGetEmployerSubmissionApprovers(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissionapprovers`;
  }

  static ipGetSharedSubmissionApprovers(): string {
    return environment.mainServiceUrl + `/odata/internal/sharedsubmissionapprovers`;
  }

  static ipGetMemberSubmissionOwners(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissionowners`;
  }

  static ipGetEmployerSubmissionOwners(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissionowners`;
  }

  static ipGetSharedSubmissionOwners(): string {
    return environment.mainServiceUrl + `/odata/internal/sharedsubmissionowners`;
  }

  static ipGetMemberSubmissionTypes(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissiontypes`;
  }

  static ipGetEmployerSubmissionTypes(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissiontypes`;
  }

  static getMemberSubmissionTypes(): string {
    return environment.mainServiceUrl + `/odata/member/submissiontypes`;
  }

  static getSubmissionRoleTemplates(): string {
    return environment.mainServiceUrl + `/odata/submissionroletemplates`;
  }

  static getAllSubmissionPermissions(): string {
    return environment.mainServiceUrl + `/odata/submissionpermissions`;
  }

  static getSubmissionPermissionTypes(): string {
    return environment.mainServiceUrl + `/submissionpermissiontypes`;
  }

  static ipUpdateTransferInTransferRecord(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/transferrecord`;
  }

  static ipUpdateMemberSubmissionOwner(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/owner`;
  }

  static ipUpdateEmployerSubmissionOwner(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/owner`;
  }

  static ipUpdateMemberSubmissionApprover(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/approver`;
  }

  static ipUpdateEmployerSubmissionApprover(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/approver`;
  }

  static ipCreateMemberSubmissionReminder(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/reminders`;
  }

  static ipCreateEmployerSubmissionReminder(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/reminders`;
  }

  static ipDeleteMemberSubmissionReminder(submissionId: string, reminderId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/reminders/${reminderId}`;
  }

  static ipDeleteEmployerSubmissionReminder(submissionId: string, reminderId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/reminders/${reminderId}`;
  }

  static ipGetMemberRecentSubmissions(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/recentsubmissions`;
  }

  static ipGetEmployerRecentSubmissions(employerId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/recentsubmissions`;
  }

  static ipDeleteVariableBenefitSubmission(submissionId: number): string {
    return (
      environment.mainServiceUrl + `/internal/membersubmissions/variablebenefitconfiguration/${submissionId}/delete`
    );
  }

  static ipDeleteVariableBenefitConfigurationConsentWaiver(submissionId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/membersubmissions/variablebenefitconfiguration/${submissionId}/consentwaiver/delete`
    );
  }

  static ipSaveVariableBenefitConfigurationSubmission(): string {
    return environment.mainServiceUrl + '/internal/membersubmissions/variablebenefitconfiguration/save';
  }

  static ipSubmitVariableBenefitConfigurationSubmission(): string {
    return environment.mainServiceUrl + '/internal/membersubmissions/variablebenefitconfiguration/submit';
  }

  static ipGetEstimatedAnnualPaymentTotalForVbConfiguration(): string {
    return (
      environment.mainServiceUrl + '/internal/membersubmissions/variablebenefitconfiguration/estimatedannualpayment'
    );
  }

  static ipUpdateMemberVariableBenefitConfigSubmission(): string {
    return environment.mainServiceUrl + '/internal/membersubmissions/updatevariablebenefitconfiguration';
  }

  static ipSubmitAnnuitySetupSubmission(): string {
    return environment.mainServiceUrl + '/internal/membersubmissions/annuitysetup';
  }

  static ipCreateMemberVariableBenefitPaymentChangeSubmission(): string {
    return environment.mainServiceUrl + '/internal/membersubmissions/variablebenefitpaymentchange';
  }

  static mpCreateMemberVariableBenefitPaymentChangeSubmission(): string {
    return environment.mainServiceUrl + '/member/submissions/variablebenefitpaymentchange';
  }

  static ipCreateUpdateEmployerAddressSubmission(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/updateaddress`;
  }

  static epCreateUpdateEmployerAddressSubmission(): string {
    return environment.mainServiceUrl + `/employer/submissions/updateaddress`;
  }

  static ipCreateUpdateEmploymentStatusSubmission(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/updateemploymentstatus`;
  }

  static epCreateUpdateEmploymentStatusSubmission(): string {
    return environment.mainServiceUrl + `/employer/submissions/updateemploymentstatus`;
  }

  static ipGetApprovedSubmissionsForMember(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/approvedsubmissionids`;
  }

  static ipGetMemberFundingBlockingSubmission(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/fundingblockingsubmission`;
  }

  static ipGetMemberRetirementProductSubmissionAvailability(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/retirementproductsubmissionavailability`;
  }

  static mpGetRetirementProductSubmissionAvailability(): string {
    return environment.mainServiceUrl + `/member/retirementproductsubmissionavailability`;
  }

  // Alerts

  static createAlert(): string {
    return environment.mainServiceUrl + `/alerts`;
  }

  static updateAlert(alertId: string): string {
    return environment.mainServiceUrl + `/alerts/${alertId}`;
  }

  static getAlertById(alertId: string): string {
    return environment.mainServiceUrl + `/odata/alerts/${alertId}`;
  }

  static getAlerts(): string {
    return environment.mainServiceUrl + `/odata/alerts`;
  }

  static getActiveAlerts(): string {
    return environment.mainServiceUrl + `/odata/activealerts`;
  }

  // Transactions

  static getInternalTransactionById(id: number): string {
    return environment.mainServiceUrl + `/odata/internal/transactions/${id}`;
  }

  static getTransactionsForRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/transactions`;
  }

  static getInternalTransactionEntriesForMemberAccount(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accounts/${accountId}/transactionentries`;
  }

  static mpGetTransactionEntriesForAccountType(accountTypeId: AccountTypeIdEnum): string {
    return environment.mainServiceUrl + `/odata/member/accounttypetransactionentries?accountTypeId=${accountTypeId}`;
  }

  static getIPTransactionEntriesForTransaction(transactionId: number): string {
    return environment.mainServiceUrl + `/odata/internal/transactions/${transactionId}/entries`;
  }

  // Accounts
  static getAccountById(accountId: number): string {
    return environment.mainServiceUrl + `/odata/accounts/${accountId}`;
  }

  static getAccountEntries(accountId: number): string {
    return environment.mainServiceUrl + `/odata/accounts/${accountId}/transactionentries`;
  }

  static mpGetAccounts(): string {
    return environment.mainServiceUrl + `/odata/member/accounts`;
  }

  static mpGetInvestmentInstructionsForAccountType(
    accountTypeId: AccountTypeIdEnum,
    excludeZeroPercentFunds: boolean
  ): string {
    return (
      environment.mainServiceUrl +
      `/member/accounts/investmentinstructions?accountTypeId=${accountTypeId}&excludeZeroPercentFunds=${excludeZeroPercentFunds}`
    );
  }

  static mpConfirmDefaultInvestmentInstructions(): string {
    return environment.mainServiceUrl + `/member/contributionaccounts/confirmdefaultinvestmentinstructions`;
  }

  static getMPContributionAccountBalances(): string {
    return environment.mainServiceUrl + `/member/contributionaccounts/contributionbalances`;
  }

  static mpGetFundDetailsForAccountType(accountTypeId: AccountTypeIdEnum): string {
    return environment.mainServiceUrl + `/member/accounts/funddetails?accountTypeId=${accountTypeId}`;
  }

  static mpGetFundAllocationsForAccountType(accountTypeId: AccountTypeIdEnum, excludeCash: boolean): string {
    return (
      environment.mainServiceUrl +
      `/member/accounts/allocationoffunds?accountTypeId=${accountTypeId}&excludeCash=${excludeCash}`
    );
  }

  static ipGetMemberAccountById(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accounts/${accountId}`;
  }

  static ipGetMemberAccounts(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accounts`;
  }

  static ipGetMemberAccountAllocationOfFunds(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/accountallocationoffunds`;
  }

  static ipGetAccountDetailsForVbAccount(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitaccountdetails`;
  }

  static mpGetAccountDetailsForVbAccount(): string {
    return environment.mainServiceUrl + `/member/variablebenefitaccountdetails`;
  }

  static getIPMemberAccountTypeBalancesByFund(memberId: number, accountTypeId: AccountTypeIdEnum): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/accounttypebalancesbyfund?accountTypeId=${accountTypeId}`
    );
  }

  static getIPContributionBalancesForAccount(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/accounts/${accountId}/contributionbalances`;
  }

  static ipGetMemberAccountFundDetails(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/accounts/${accountId}/funddetails`;
  }

  static mpGetAnnuityAccountDetails(accountId: number): string {
    return environment.mainServiceUrl + `/member/annuityaccounts/${accountId}/details`;
  }

  static ipGetMemberAnnuityAccountDetails(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/annuityaccounts/${accountId}/details`;
  }

  static mpGetAnnuityEntitlements(): string {
    return environment.mainServiceUrl + `/member/annuityentitlements`;
  }

  static ipGetMemberAnnuityEntitlements(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/annuityentitlements`;
  }

  static ipUpdateMemberAnnuityPaymentInstructionDepositInstructions(
    memberId: number,
    accountId: number,
    paymentInstructionId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/annuityaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/depositinstructions`
    );
  }

  static ipTerminateMemberAnnuityPaymentInstruction(
    memberId: number,
    accountId: number,
    paymentInstructionId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/annuityaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/terminate`
    );
  }

  static ipSuspendMemberAnnuityPaymentInstruction(
    memberId: number,
    accountId: number,
    paymentInstructionId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/annuityaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/suspend`
    );
  }

  static ipResumeMemberAnnuityPaymentInstruction(
    memberId: number,
    accountId: number,
    paymentInstructionId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/annuityaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/resume`
    );
  }

  static ipEngageMemberAnnuityJlsProperties(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/annuityaccounts/${accountId}/engagejls`;
  }

  static ipSplitMemberAnnuity(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/annuityaccounts/${accountId}/split`;
  }

  static mpUpdateAnnuityPaymentInstructionDepositInstructions(accountId: number, paymentInstructionId: string): string {
    return (
      environment.mainServiceUrl +
      `/member/annuityaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/depositinstructions`
    );
  }

  static ipGetMemberContributionAccountInvestmentInstructions(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/investmentinstructions?accountTypeId=1`;
  }

  static ipGetMemberInvestmentInstructionsForAccountType(memberId: number, accountId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/investmentinstructions?accountTypeId=${accountId}`
    );
  }

  // Portal Agnostic
  static getVbMinMaxWithdrawalAmounts(): string {
    return environment.mainServiceUrl + `/variablebenefitminmaxwithdrawalamounts`;
  }

  static getLifeOnlyAnnuityCalculation(): string {
    return environment.mainServiceUrl + `/annuitylifeonlycalculations`;
  }

  static getJointLifeAnnuityCalculation(): string {
    return environment.mainServiceUrl + `/annuityjointlifecalculations`;
  }

  static getRefundLifeAnnuityCalculation(): string {
    return environment.mainServiceUrl + `/annuityrefundlifecalculations`;
  }

  static getAnnuityOperationalConversionRateForDate(date: string): string {
    return environment.mainServiceUrl + `/internal/annuityoperationalconversionratefordate?date=${date}`;
  }

  static ipGetCurrentYearSmallPensionMonthlyLimit(): string {
    return environment.mainServiceUrl + `/internal/smallpensionmonthlylimits/currentyear`;
  }

  static ipGetMemberVbAccountActivityBreakdown(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitaccount/activitybreakdown`;
  }

  static mpGetVbAccountActivityBreakdown(): string {
    return environment.mainServiceUrl + `/member/variablebenefitaccount/activitybreakdown`;
  }

  static ipGetEstimatedAnnualPaymentTotalForVbAccount(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitaccount/estimatedannualpayment`;
  }

  static mpGetEstimatedAnnualPaymentTotalForVbAccount(): string {
    return environment.mainServiceUrl + `/member/variablebenefitaccount/estimatedannualpayment`;
  }

  static ipGetUpcomingPaymentsForVbAccount(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitaccount/upcomingpayments`;
  }

  static mpGetUpcomingPaymentsForVbAccount(): string {
    return environment.mainServiceUrl + `/member/variablebenefitaccount/upcomingpayments`;
  }

  static ipGetWithdrawalInstructionsForVbAccount(memberId: number, excludeZeroPercentFunds = false): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/variablebenefitaccount/withdrawalinstructions?excludeZeroPercentFunds=${excludeZeroPercentFunds}`
    );
  }

  static mpGetWithdrawalInstructionsForVbAccount(excludeZeroPercentFunds = false): string {
    return (
      environment.mainServiceUrl +
      `/member/variablebenefitaccount/withdrawalinstructions?excludeZeroPercentFunds=${excludeZeroPercentFunds}`
    );
  }

  static ipGetMemberBankAccountById(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/bankaccounts/${accountId}`;
  }

  static ipGetVbOutboundPaymentCutoffDate(): string {
    return environment.mainServiceUrl + `/internal/outboundpaymentallowablechangedate`;
  }

  static mpGetVbOutboundPaymentCutoffDate(): string {
    return environment.mainServiceUrl + `/member/outboundpaymentallowablechangedate`;
  }

  // Release Notes

  static getReleaseNoteDocuments(): string {
    return environment.mainServiceUrl + `/odata/releasenotedocuments`;
  }

  static getReleaseNoteDocumentVersions(): string {
    return environment.mainServiceUrl + `/odata/releasenotedocumentversions`;
  }

  static getReleaseNoteDocumentById(id: string): string {
    return environment.mainServiceUrl + `/odata/releasenotedocuments/${id}`;
  }

  static createReleaseNoteDocument(): string {
    return environment.mainServiceUrl + `/releasenotedocuments`;
  }

  static releaseNoteDocumentById(id: string): string {
    return environment.mainServiceUrl + `/releasenotedocuments/${id}`;
  }

  static getLatestReleaseNoteDocument(): string {
    return environment.mainServiceUrl + `/odata/latestreleasenotedocument`;
  }

  static getUniqueReleaseVersions(): string {
    return environment.mainServiceUrl + `/odata/releasenotedocumentversions`;
  }

  // Remittances

  static getRemittanceStatusTypes(): string {
    return environment.mainServiceUrl + `/remittancestatustypes`;
  }

  static getRemittancePaymentStatusTypes(): string {
    return environment.mainServiceUrl + `/remittancepaymentstatustypes`;
  }

  static getPaymentMethodTypes(): string {
    return environment.mainServiceUrl + `/paymentmethodtypes`;
  }

  static getInternalOpenRemittancesForEmployer(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/openremittances`;
  }

  static getOpenRemittancesForEmployer(): string {
    return environment.mainServiceUrl + `/odata/employer/openremittances`;
  }

  static getInternalClosedRemittancesForEmployer(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/closedremittances`;
  }

  static getClosedRemittancesForEmployer(): string {
    return environment.mainServiceUrl + `/odata/employer/closedremittances`;
  }

  static createRemittance(): string {
    return environment.mainServiceUrl + `/remittances`;
  }

  static submitRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/submit`;
  }

  static getRemittanceById(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}`;
  }

  static getNewMembersOnRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/newmembers`;
  }

  static createRemittanceNewMember(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/newmemberdetails`;
  }

  static updateRemittanceNewMember(remittanceId: number, contributionId: string, newMemberId: string): string {
    return (
      environment.mainServiceUrl +
      `/remittances/${remittanceId}/contributions/${contributionId}/newmemberdetails/${newMemberId}`
    );
  }

  static getRemittanceHistory(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/history`;
  }

  static getHistoryForContribution(remittanceId: number, contributionId: string): string {
    return (
      environment.mainServiceUrl +
      `/odata/remittances/${remittanceId}/contributions/${contributionId}/history?$expand=OriginPortal`
    );
  }

  static getHistoryForScheduledContribution(scheduleId: number, contributionId: string): string {
    return (
      environment.mainServiceUrl +
      // eslint-disable-next-line max-len
      `/odata/scheduledremittances/${scheduleId}/contributions/${contributionId}/history?$expand=OriginPortal&$orderby=eventDate desc`
    );
  }

  static getRemittanceOwners(): string {
    return environment.mainServiceUrl + `/odata/remittanceowners`;
  }

  static getCommentsForRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/comments`;
  }

  static addRemittanceComment(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/comments`;
  }

  static addTaxationYearToRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/taxationyear`;
  }

  static addTaxationYearToScheduledRemittance(scheduleId: number): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduleId}/taxationyear`;
  }

  static getRemittanceMembersToVerify(remittanceId: string): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/memberstoverify`;
  }

  static deleteRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}`;
  }

  static updateRemittanceOwner(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updateowner`;
  }

  static updateRemittanceStatusInReview(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updatestatusinreview`;
  }

  static updateRemittanceStatusApproved(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updatestatusapproved`;
  }

  static updateRemittanceStatusFailed(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updatestatusfailed`;
  }

  static getAllRemittances(): string {
    return environment.mainServiceUrl + `/odata/remittances`;
  }

  // Scheduled Remittances

  static createScheduledRemittance(): string {
    return environment.mainServiceUrl + `/scheduledremittances`;
  }

  static getScheduledRemittances(): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances`;
  }

  static getScheduledRemittanceById(id: number): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances/${id}`;
  }

  static updateScheduledRemittance(id: number): string {
    return environment.mainServiceUrl + `/scheduledremittances/${id}`;
  }

  static getOverlappingScheduledRemittanceCount(
    employerId: number,
    frequencyId: RemittanceFrequencyEnum,
    startDate: string
  ): string {
    const url =
      `/odata/overlappingscheduledremittances?$count=true&$top=0&employerId=${employerId}` +
      `&frequencyId=${frequencyId}&startDate=${startDate}`;
    return environment.mainServiceUrl + url;
  }

  static getNextFiveRemittanceDates(frequencyId: RemittanceFrequencyEnum, startDate: string, endDate?: string): string {
    const baseUrl =
      environment.mainServiceUrl +
      `/scheduledremittances/nextfivedates?frequencyId=${frequencyId}&startDate=${startDate}`;
    return endDate ? `${baseUrl}&endDate=${endDate}` : baseUrl;
  }

  static getScheduledRemittanceFrequencyTypes(): string {
    return environment.mainServiceUrl + `/scheduledremittancefrequencytypes`;
  }

  static getScheduledRemittanceStatusTypes(): string {
    return environment.mainServiceUrl + `/scheduledremittancestatustypes`;
  }

  static getScheduleMembersToVerify(scheduleId: string): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances/${scheduleId}/memberstoverify`;
  }

  static getNewMembersOnScheduledRemittance(scheduledRemittanceId: number): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances/${scheduledRemittanceId}/newmembers`;
  }

  static createScheduledRemittanceNewMember(scheduledRemittanceId: number, contributionId: string): string {
    return (
      environment.mainServiceUrl +
      `/scheduledremittances/${scheduledRemittanceId}/contributions/${contributionId}/newmemberdetails`
    );
  }

  static updateScheduledRemittanceNewMember(
    scheduledRemittanceId: number,
    contributionId: string,
    newMemberId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/scheduledremittances/${scheduledRemittanceId}/contributions/${contributionId}/newmemberdetails/${newMemberId}`
    );
  }

  static addScheduledRemittanceComment(scheduledRemittanceId: number): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduledRemittanceId}/comments`;
  }

  static getCommentsForScheduledRemittance(scheduledRemittanceId: number): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances/${scheduledRemittanceId}/comments`;
  }

  static getScheduledRemittanceHistory(scheduledRemittanceId: number): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances/${scheduledRemittanceId}/history`;
  }

  static deleteScheduledRemittance(scheduledRemittanceId: number): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduledRemittanceId}`;
  }

  static cancelScheduledRemittance(scheduledRemittanceId: number): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduledRemittanceId}/cancel`;
  }

  static setScheduledRemittanceToScheduled(scheduledRemittanceId: number): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduledRemittanceId}/updatestatusscheduled`;
  }

  // Payments

  static createRemittancePayment() {
    return environment.mainServiceUrl + `/payments`;
  }

  // Contributions

  static getContributionsForRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/contributions`;
  }

  static getContributionsForScheduledRemittance(scheduleId: number): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances/${scheduleId}/contributions`;
  }

  static getContributionSummaryForRemittance(remittanceId: string): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/contributionsummary`;
  }

  static getContributionSummaryForScheduledRemittance(scheduleId: string): string {
    return environment.mainServiceUrl + `/odata/scheduledremittances/${scheduleId}/contributionsummary`;
  }

  static addContributionToRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions`;
  }

  static addContributionToScheduledRemittance(scheduleId: number): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduleId}/contributions`;
  }

  static deleteContribution(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}`;
  }

  static deleteScheduledRemittanceContribution(scheduleId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduleId}/contributions/${contributionId}`;
  }

  // Contribution Entries

  static createContributionEntry(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/entries`;
  }

  static createScheduledRemittanceContributionEntry(scheduleId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduleId}/contributions/${contributionId}/entries`;
  }

  static updateContributionEntry(remittanceId: number, contributionId: string, entryId: string): string {
    return (
      environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/entries/${entryId}`
    );
  }

  static updateScheduledRemittanceContributionEntry(
    scheduleId: number,
    contributionId: string,
    entryId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/scheduledremittances/${scheduleId}/contributions/${contributionId}/entries/${entryId}`
    );
  }

  static deleteContributionEntry(remittanceId: number, contributionId: string, entryId: string): string {
    return (
      environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/entries/${entryId}`
    );
  }

  static deleteScheduledRemittanceContributionEntry(
    scheduleId: number,
    contributionId: string,
    entryId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/scheduledremittances/${scheduleId}/contributions/${contributionId}/entries/${entryId}`
    );
  }

  static getPotentialMatchesForContribution(remittanceId: string, contributionId: string): string {
    return (
      environment.mainServiceUrl +
      `/odata/remittances/${remittanceId}/contributions/${contributionId}/potentialmembermatches?$count=true`
    );
  }

  static getPotentialMatchesForScheduledContribution(scheduleId: string, contributionId: string): string {
    return (
      environment.mainServiceUrl +
      `/odata/scheduledremittances/${scheduleId}/contributions/${contributionId}/potentialmembermatches?$count=true`
    );
  }

  static verifyContribution(remittanceId: string, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/verify`;
  }

  static verifyScheduleContribution(scheduleId: string, contributionId: string): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduleId}/contributions/${contributionId}/verify`;
  }

  static updateContribution(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}`;
  }

  static updateScheduleContribution(scheduleId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/scheduledremittances/${scheduleId}/contributions/${contributionId}`;
  }

  // Beneficiaries

  static ipGetMemberAccountTypeBeneficiaries(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accounttypebeneficiaries`;
  }

  static ipGetMemberBeneficiaries(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/beneficiaries`;
  }

  static ipGetMemberBeneficiary(memberId: number, beneficiaryId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/beneficiaries/${beneficiaryId}`;
  }

  static ipUpdateMemberBeneficiary(memberId: number, beneficiaryId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/beneficiaries/${beneficiaryId}`;
  }

  static mpGetMemberAccountTypeBeneficiaries(): string {
    return environment.mainServiceUrl + `/odata/member/accounttypebeneficiaries`;
  }

  static mpGetMemberBeneficiaries(): string {
    return environment.mainServiceUrl + `/odata/member/beneficiaries`;
  }

  static mpGetMemberBeneficiary(beneficiaryId: number): string {
    return environment.mainServiceUrl + `/odata/member/beneficiaries/${beneficiaryId}`;
  }

  static mpUpdateMemberBeneficiary(beneficiaryId: number): string {
    return environment.mainServiceUrl + `/member/beneficiaries/${beneficiaryId}`;
  }

  // Banking

  static mpArchiveBankAccount(bankAccountId: number): string {
    return environment.mainServiceUrl + `/member/bankaccounts/${bankAccountId}/archive`;
  }

  static ipArchiveBankAccount(memberId: number, bankAccountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/bankaccounts/${bankAccountId}/archive`;
  }

  static mpGetBankAccounts(): string {
    return environment.mainServiceUrl + `/member/bankaccounts`;
  }

  static ipGetBankAccounts(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/bankaccounts`;
  }

  static mpGetBankAccountUsage(bankAccountId: number): string {
    return environment.mainServiceUrl + `/member/bankaccounts/${bankAccountId}/usage`;
  }

  static ipGetBankAccountUsage(memberId: number, bankAccountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/bankaccounts/${bankAccountId}/usage`;
  }

  // Self Enrolment
  static mpSelfEnroll(): string {
    return environment.mainServiceUrl + `/member/selfenrol`;
  }

  // Consent Waivers
  static ipGetConsentWaiversForMember(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/consentwaivers`;
  }

  static ipMemberConsentWaiverById(memberId: number, consentWaiverId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/consentwaivers/${consentWaiverId}`;
  }

  static ipGetConsentWaiverTypes(): string {
    return environment.mainServiceUrl + `/memberconsentwaivertypes`;
  }

  static ipCreateConsentWaiverForMember(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/consentwaivers`;
  }

  // Taxes

  static ipGetTaxForms(): string {
    return environment.mainServiceUrl + `/odata/internal/taxforms`;
  }

  static ipGetPrescribedTaxZoneConfigurations(): string {
    return environment.mainServiceUrl + `/odata/internal/prescribedtaxzoneconfigurations`;
  }

  static ipGetNonResidentTaxWithholdingTreatyRates(): string {
    return environment.mainServiceUrl + `/odata/internal/nonresidenttaxwithholdingtreatyrates`;
  }

  static ipGetTaxForm(taxFormId: number): string {
    return environment.mainServiceUrl + `/odata/internal/taxforms/${taxFormId}`;
  }

  static ipGetTaxFormFields(taxFormId: number): string {
    return environment.mainServiceUrl + `/odata/internal/taxforms/${taxFormId}/formfields`;
  }

  static ipGetMemberTaxProfiles(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/taxprofiles`;
  }

  static ipGetMemberTaxProfile(memberId: number, memberTaxProfileId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}`;
  }

  static ipCreateMemberTaxProfile(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/taxprofiles`;
  }

  static ipCreateMemberTaxProfileCraLetterReference(memberId: number, memberTaxProfileId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}/craletterreferences`
    );
  }

  static ipCreateMemberTaxProfileCraLetterReferenceAttachment(memberId: number, memberTaxProfileId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}/craletterreferenceattachments`
    );
  }

  static ipCreateMemberTaxProfileSupportingDocumentationAttachment(
    memberId: number,
    memberTaxProfileId: number
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}/supportingdocumentationattachments`
    );
  }

  static getTaxFormTypes(): string {
    return environment.mainServiceUrl + `/taxformtypes`;
  }

  static getTaxResidencyTypes(): string {
    return environment.mainServiceUrl + `/taxresidencytypes`;
  }

  static getTaxStatusTypes(): string {
    return environment.mainServiceUrl + `/taxstatustypes`;
  }

  static getTaxProfileSourceTypes(): string {
    return environment.mainServiceUrl + `/taxprofilesourcetypes`;
  }

  static getPrescribedTaxZoneTypes(): string {
    return environment.mainServiceUrl + `/prescribedtaxzonetypes`;
  }

  static getTaxWithholdingOverrideTypes(): string {
    return environment.mainServiceUrl + `/taxwithholdingoverridetypes`;
  }

  static getTaxCraLetterOfAuthorityTypes(): string {
    return environment.mainServiceUrl + `/taxcraletterofauthoritytypes`;
  }

  // Shift4

  static initiateI4GoAccess(): string {
    return environment.mainServiceUrl + `/shift4/initiatei4goaccess`;
  }

  static ipRefundShift4Payment(invoiceId: string): string {
    return environment.mainServiceUrl + `/member/shift4payments/${invoiceId}/refunds`;
  }

  static ipVoidShift4Payment(invoiceId: string): string {
    return environment.mainServiceUrl + `/member/shift4payments/${invoiceId}/void`;
  }
}
